import { Text, View, Link, Image, StyleSheet } from '@react-pdf/renderer';
import { sharedStyles, pdfColors, isAccessibleUrl } from './sharedStyles';
import ContentRenderer from './ContentRenderer';
import type { AssessmentReportAssessmentQuestion, AssessmentReportFile } from './types';

interface FileUploadPromptItemProps {
  question: AssessmentReportAssessmentQuestion;
  userInput: string;
  correct: boolean;
  files: AssessmentReportFile[];
  feedback?: string | null;
}

const styles = StyleSheet.create({
  filesContainer: {
    marginTop: 8,
  },
  fileItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 8,
    marginBottom: 6,
    backgroundColor: pdfColors.background,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: pdfColors.border,
  },
  fileIcon: {
    width: 24,
    height: 24,
    marginRight: 8,
    backgroundColor: pdfColors.link,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  fileIconText: {
    fontSize: 8,
    color: pdfColors.white,
    fontFamily: 'Inter-SemiBold',
  },
  fileDetails: {
    flex: 1,
  },
  filename: {
    fontSize: 10,
    color: pdfColors.text,
    marginBottom: 2,
  },
  fileSize: {
    fontSize: 8,
    color: pdfColors.textMuted,
  },
  downloadLink: {
    fontSize: 9,
    color: pdfColors.link,
    textDecoration: 'underline',
  },
  unavailableText: {
    fontSize: 9,
    color: pdfColors.textMuted,
    fontStyle: 'italic',
  },
  imagePreview: {
    width: '100%',
    maxWidth: 500,
    maxHeight: 400,
    marginBottom: 8,
    borderRadius: 4,
  },
  imageContainer: {
    marginBottom: 8,
    width: '100%',
    alignItems: 'center',
  },
  imagePlaceholder: {
    width: '100%',
    maxWidth: 500,
    height: 200,
    marginBottom: 8,
    backgroundColor: pdfColors.backgroundMuted,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: pdfColors.borderMuted,
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePlaceholderText: {
    fontSize: 10,
    color: pdfColors.textMuted,
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  videoPlaceholder: {
    width: '100%',
    maxWidth: 500,
    height: 250,
    marginBottom: 8,
    backgroundColor: pdfColors.textHeading,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  videoIcon: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: pdfColors.videoIconBackground,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  videoIconText: {
    fontSize: 24,
    color: pdfColors.white,
  },
  videoPlaceholderText: {
    fontSize: 11,
    color: pdfColors.white,
    textAlign: 'center',
    paddingHorizontal: 20,
    fontFamily: 'Inter-SemiBold',
  },
  videoSubtext: {
    fontSize: 10,
    color: pdfColors.textSubtle,
    textAlign: 'center',
    paddingHorizontal: 20,
    marginTop: 4,
  },
});

function formatFileSize(bytes: number | null): string {
  if (bytes == null || bytes === 0) {
    return '0 B';
  }

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`;
}

function getFileExtension(filename: string): string {
  const parts = filename.split('.');
  if (parts.length > 1) {
    const ext = parts.at(-1);
    if (ext != null) {
      return ext.substring(0, 3).toUpperCase();
    }
  }
  return 'FILE';
}

function isImageFile(mimetype: string): boolean {
  return mimetype.startsWith('image/');
}

function isVideoFile(mimetype: string): boolean {
  return mimetype.startsWith('video/');
}

function getFileIconColor(mimetype: string): string {
  if (mimetype.startsWith('image/')) {
    return pdfColors.success;
  }
  if (mimetype.startsWith('video/')) {
    return pdfColors.violet;
  }
  if (mimetype === 'application/pdf') {
    return pdfColors.error;
  }
  if (
    mimetype === 'application/msword' ||
    mimetype === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    return pdfColors.info;
  }
  if (
    mimetype === 'application/vnd.ms-excel' ||
    mimetype === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  ) {
    return pdfColors.success;
  }
  if (
    mimetype === 'application/vnd.ms-powerpoint' ||
    mimetype === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
  ) {
    return pdfColors.orange;
  }
  return pdfColors.link;
}

function FileUploadPromptItem({
  question,
  userInput,
  correct,
  files,
  feedback,
}: FileUploadPromptItemProps): React.ReactElement {
  return (
    <View style={sharedStyles.itemContainer}>
      <View style={sharedStyles.itemHeader}>
        <Text style={sharedStyles.itemTitle}>
          {question.position}. {question.title}
        </Text>
        <View style={correct ? sharedStyles.resultBadgeCorrect : sharedStyles.resultBadgeIncorrect}>
          <Text style={sharedStyles.resultBadgeText}>{correct ? 'CORRECT' : 'INCORRECT'}</Text>
        </View>
      </View>

      {question.questionContentFormatted && question.questionContentFormatted.length > 0 ? (
        <ContentRenderer content={question.questionContentFormatted} />
      ) : null}

      {files != null && files.length > 0 ? (
        <View style={styles.filesContainer}>
          {files.map((file) => (
            <View key={file.submissionFileId}>
              {isImageFile(file.mimetype) ? (
                isAccessibleUrl(file.url) ? (
                  <View style={styles.imageContainer}>
                    <Image src={file.url} style={styles.imagePreview} cache={false} />
                  </View>
                ) : (
                  <View style={styles.imagePlaceholder}>
                    <Text style={styles.imagePlaceholderText}>
                      Image preview unavailable in development environment.{'\n'}
                      Use download link to view file.
                    </Text>
                  </View>
                )
              ) : isVideoFile(file.mimetype) ? (
                <View style={styles.videoPlaceholder}>
                  <View style={styles.videoIcon}>
                    <Text style={styles.videoIconText}>▶</Text>
                  </View>
                  <Text style={styles.videoPlaceholderText}>Video File</Text>
                  <Text style={styles.videoSubtext}>Use download link to view</Text>
                </View>
              ) : null}
              <View style={styles.fileItem}>
                <View
                  style={[styles.fileIcon, { backgroundColor: getFileIconColor(file.mimetype) }]}
                >
                  <Text style={styles.fileIconText}>{getFileExtension(file.filename)}</Text>
                </View>
                <View style={styles.fileDetails}>
                  <Text style={styles.filename}>{file.filename}</Text>
                  <Text style={styles.fileSize}>{formatFileSize(file.sizeInBytes)}</Text>
                </View>
                {file.url ? (
                  <Link src={file.url} style={styles.downloadLink}>
                    Download
                  </Link>
                ) : (
                  <Text style={styles.unavailableText}>Unavailable</Text>
                )}
              </View>
            </View>
          ))}
        </View>
      ) : (
        <View style={sharedStyles.mediaPlaceholder}>
          <Text style={sharedStyles.mediaPlaceholderText}>No files uploaded</Text>
        </View>
      )}

      {userInput ? (
        <View style={sharedStyles.answerSection} wrap={false}>
          <Text style={sharedStyles.answerText}>{userInput}</Text>
        </View>
      ) : null}

      {feedback != null && feedback.length > 0 ? (
        <View style={sharedStyles.feedbackSection} wrap={false}>
          <Text style={sharedStyles.feedbackLabel}>ASSESSOR FEEDBACK</Text>
          <Text style={sharedStyles.feedbackText}>{feedback}</Text>
        </View>
      ) : null}
    </View>
  );
}

export default FileUploadPromptItem;
